import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarDays, Clock, User, Stethoscope, CheckCircle2, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  requested: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');
  const [viewDialog, setViewDialog] = useState(null);
  const [formSubmission, setFormSubmission] = useState(null);

  const fetchAppointments = async () => {
    try {
      let url = '/appointments?limit=100';
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await API.get(url);
      setAppointments(res.data.appointments || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAppointments(); }, [statusFilter]);

  const confirmAppt = async () => {
    if (!confirmDialog) return;
    try {
      await API.post(`/appointments/${confirmDialog.id}/confirm`, { date: confirmDate, time: confirmTime });
      toast.success('Appointment confirmed');
      setConfirmDialog(null);
      fetchAppointments();
    } catch (err) { toast.error('Failed to confirm'); }
  };

  const completeAppt = async (id) => {
    try {
      await API.post(`/appointments/${id}/complete`);
      toast.success('Appointment completed. Follow-up scheduled if enabled.');
      fetchAppointments();
    } catch (err) { toast.error('Failed to complete'); }
  };

  const viewApptDetails = async (appt) => {
    setViewDialog(appt);
    if (appt.form_submission_id) {
      try {
        const res = await API.get(`/surveys/submissions/${appt.form_submission_id}`);
        setFormSubmission(res.data);
      } catch { setFormSubmission(null); }
    } else {
      setFormSubmission(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="appointments-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Appointments</h1>
          <p className="text-slate-500 mt-1">{total} appointments</p>
        </div>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="appt-status-filter" className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="requested">Requested</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Patient</TableHead>
                <TableHead className="font-semibold text-slate-700">Doctor</TableHead>
                <TableHead className="font-semibold text-slate-700">Date & Time</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700">Mode</TableHead>
                <TableHead className="font-semibold text-slate-700">Forms</TableHead>
                <TableHead className="font-semibold text-slate-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No appointments found</p></TableCell></TableRow>
              ) : appointments.map(appt => (
                <TableRow key={appt.id} className="hover:bg-slate-50" data-testid={`appt-row-${appt.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                      <span className="font-medium text-slate-900">{appt.patient_name || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-sky-500" />
                      <div>
                        <p className="text-sm font-medium">{appt.doctor_name || 'Unassigned'}</p>
                        {appt.doctor_specialty && <p className="text-xs text-slate-500">{appt.doctor_specialty}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {appt.date ? (
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm">{appt.date} {appt.time && `at ${appt.time}`}</span>
                      </div>
                    ) : <span className="text-xs text-slate-400">Pending confirmation</span>}
                  </TableCell>
                  <TableCell><Badge className={STATUS_COLORS[appt.status] || 'bg-slate-100'}>{appt.status}</Badge></TableCell>
                  <TableCell><span className="text-xs text-slate-500 capitalize">{appt.booking_mode || 'request'}</span></TableCell>
                  <TableCell>
                    {appt.form_submission_id ? (
                      <Badge className="bg-emerald-100 text-emerald-700 cursor-pointer" onClick={() => viewApptDetails(appt)}>
                        <FileText className="w-3 h-3 mr-1" /> Filled
                      </Badge>
                    ) : <span className="text-xs text-slate-400">None</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {appt.status === 'requested' && (
                        <Button size="sm" variant="outline" className="text-sky-600" onClick={() => { setConfirmDialog(appt); setConfirmDate(appt.date || ''); setConfirmTime(appt.time || ''); }} data-testid={`confirm-appt-${appt.id}`}>
                          Confirm
                        </Button>
                      )}
                      {appt.status === 'confirmed' && (
                        <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => completeAppt(appt.id)} data-testid={`complete-appt-${appt.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Complete
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => viewApptDetails(appt)}>View</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Confirm Appointment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Confirming appointment for <strong>{confirmDialog?.patient_name}</strong> with <strong>{confirmDialog?.doctor_name}</strong></p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={confirmDate} onChange={e => setConfirmDate(e.target.value)} data-testid="confirm-date" /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={confirmTime} onChange={e => setConfirmTime(e.target.value)} data-testid="confirm-time" /></div>
            </div>
            <Button onClick={confirmAppt} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="confirm-appt-submit">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewDialog} onOpenChange={() => { setViewDialog(null); setFormSubmission(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Appointment Details</DialogTitle></DialogHeader>
          {viewDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Patient:</span> <strong>{viewDialog.patient_name}</strong></div>
                <div><span className="text-slate-500">Doctor:</span> <strong>{viewDialog.doctor_name}</strong></div>
                <div><span className="text-slate-500">Date:</span> {viewDialog.date || 'TBD'}</div>
                <div><span className="text-slate-500">Time:</span> {viewDialog.time || 'TBD'}</div>
                <div><span className="text-slate-500">Status:</span> <Badge className={STATUS_COLORS[viewDialog.status]}>{viewDialog.status}</Badge></div>
                <div><span className="text-slate-500">Mode:</span> {viewDialog.booking_mode}</div>
              </div>
              {viewDialog.notes && <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{viewDialog.notes}</p>}
              {formSubmission && (
                <div className="bg-sky-50 p-4 rounded-lg border border-sky-100">
                  <h4 className="text-sm font-semibold text-sky-800 mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4" /> Pre-Visit Form Responses</h4>
                  <div className="space-y-2">
                    {Object.entries(formSubmission.responses || {}).map(([key, val]) => (
                      <div key={key} className="text-sm"><span className="text-slate-500">{key}:</span> <span className="font-medium">{String(val)}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
